import { useIntl } from 'react-intl';
import validator from 'validator';
import * as Yup from 'yup';

import Provision from '../../api/provision';
import { FormModal } from '../../components/Formik';
import useApiQuery from '../../hooks/useApiQuery';

import { CONTACT_ROLES } from './ContactRoles';

import type { Contact } from '@/types/contactTypes';

interface CreateContactFormValues extends Record<string, unknown> {
  email: string;
  type: string;
  name: string;
  phoneNumber: string;
  slackChannel: string;
}

const UNIQUE_CONTACT_FORM_ERROR = 'uniqueContact';

const normalizeEmail = (email: string | undefined): string => email?.trim().toLowerCase() ?? '';

const isContactFormValues = (value: unknown): value is CreateContactFormValues => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const formValues = value as Partial<CreateContactFormValues>;

  return typeof formValues.email === 'string' && typeof formValues.type === 'string';
};

interface CreateContactFormModalProps {
  juiceGroup: string;
  contacts: Contact[];
  onSubmit: (data: Contact) => void;
  onCancel: () => void;
}

const CreateContactFormModal = ({
  juiceGroup,
  contacts,
  onSubmit,
  onCancel,
}: CreateContactFormModalProps): React.JSX.Element => {
  const intl = useIntl();

  const duplicateContactMessage = intl.formatMessage({
    id: 'duplicateContactValidation',
  });

  const { error: apiError, makeApiCall: createContactCall } = useApiQuery<Contact>(
    Provision.addContact,
    {
      successMessage: 'contactAdded',
    }
  );

  const fields = [
    {
      fieldType: 'text',
      name: 'email',
      label: 'Email',
    },
    {
      fieldType: 'select',
      name: 'type',
      label: 'Role',
      menuItems: Object.entries(CONTACT_ROLES).map(([key, label]) => ({ value: key, label })),
    },
    {
      fieldType: 'text',
      name: 'name',
      label: 'Name',
    },
    {
      fieldType: 'text',
      name: 'phoneNumber',
      label: 'Phone Number',
    },
    {
      fieldType: 'text',
      name: 'slackChannel',
      label: 'Slack Channel',
    },
  ];

  const initialValues: CreateContactFormValues = {
    email: '',
    type: '',
    name: '',
    phoneNumber: '',
    slackChannel: '',
  };

  const validationSchema = Yup.object()
    .shape({
      email: Yup.string().required('Required').email('Invalid email address'),
      type: Yup.string().required('Required'),
      phoneNumber: Yup.string().test(
        'is-valid-phone',
        'Invalid phone number',
        (value: string | undefined) =>
          !value || validator.isMobilePhone(value, 'any', { strictMode: true })
      ),
    })
    .test('is-unique-email-role', duplicateContactMessage, function (values: unknown) {
      if (!isContactFormValues(values)) {
        return true;
      }

      const normalizedEmail = normalizeEmail(values.email);
      const contactExists = contacts.some(
        contact => normalizeEmail(contact.email) === normalizedEmail && contact.type === values.type
      );

      return contactExists
        ? this.createError({
            path: UNIQUE_CONTACT_FORM_ERROR,
            message: duplicateContactMessage,
          })
        : true;
    });

  const createContact = async (formValues: CreateContactFormValues): Promise<void> => {
    const data = await createContactCall({
      ...formValues,
      juiceGroup,
    });

    if (data) {
      onSubmit(data);
    }
  };

  return (
    <FormModal
      title={intl.formatMessage({ id: 'createNewContact' })}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        if (isContactFormValues(values)) {
          void createContact(values);
        }
      }}
      onCancel={onCancel}
      submitLabel={intl.formatMessage({ id: 'create' })}
      formErrorName={UNIQUE_CONTACT_FORM_ERROR}
      error={apiError}
    />
  );
};

export default CreateContactFormModal;
